import {
  defaultIdMaker,
  IBbox,
  IBboxType,
  ILabel,
  ILabelType
} from 'annotation-tools/annotation-canvas';
import { SnackbarForPromise } from 'annotation-tools/ui-parts';
import {
  combineLatestTyped,
  filterNotUndefined,
  get,
  IImage,
  IImageType,
  IList,
  IRect,
  IRectType,
  mapToConst,
  mergeTyped,
  PromiseSnackbarStateType,
  toPromiseSnackbarState,
  toRelativeRect,
  useDataStream,
  useEventAsStream,
  useStateAsStream,
  useStream,
  useStreamEffect,
  useStreamValue,
  useVoidEventAsStream,
  withLatestValuesFrom
} from 'annotation-tools/utils';
import React, { FC, memo, useMemo } from 'react';
import {
  distinctUntilChanged,
  map,
  mapTo,
  scan,
  withLatestFrom
} from 'rxjs/operators';
import styled from 'styled-components';
import { executeInference } from '../../../api/fetch-functions/project-image/predict';
import { USE_MOCK_INFERENCE_RESULT } from '../../../env-values';
import { token$ } from '../../../global-state/auth-state';
import { imageId$, projectId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IAnnotation, IAnnotationType } from '../../../types/record/annotation';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { useValueAsDataStream } from '../../../utils/use-value-as-data-stream';
import { snackbarMessages } from '../../rect-tool-with-crop/constants/messages/snackbar-message';
import { ButtonStateType } from '../../rect-tool-with-crop/imports/utils';
import { RectToolWithCropMainSub } from '../../rect-tool-with-crop/rect-tool-with-crop-main-sub';
import { AppMode } from '../../rect-tool-with-crop/types/enum/app-mode';
import { useBboxList } from './hooks/use-bbox-list';
import { useCropRect } from './hooks/use-crop-rect';
import { useHandlers } from './hooks/use-handlers';
import { inferButtonStateReducer } from './infer-button-state-reducer';

import { ParentContext } from '../../rect-tool-with-crop/context/parent';
import { ParentType } from '../../rect-tool-with-crop/types/enum/parent-types';

const Root = styled.div`
  height: 100%;
  width: 100%;
`;

const generateMockInferenceResult = (labelList: IList<ILabelType>) =>
  IList<IAnnotationType>([
    IAnnotation({
      uuid: defaultIdMaker(),
      labelId: labelList.get(0, ILabel()).id as number,
      rect: IRect({
        top: 0.9 * Math.random(),
        left: 0.9 * Math.random(),
        width: 0.1,
        height: 0.1
      })
    }),
    IAnnotation({
      uuid: defaultIdMaker(),
      labelId: labelList.get(1, ILabel()).id as number,
      rect: IRect({
        top: 0.9 * Math.random(),
        left: 0.9 * Math.random(),
        width: 0.1,
        height: 0.1
      })
    })
  ]);

interface Props {
  annotationList: IList<IAnnotationType>;
  imageList: IList<IProjectImageInfoType>;
  labelList: IList<ILabelType>;
  image: IProjectImageInfoType;
  openSettingsDialog: () => void;
}

export const ProjectImageRootSub: FC<Props> = memo((props: Readonly<Props>) => {
  /* prop stream */
  const annotationListFiltered = useMemo(
    () =>
      props.annotationList.filter(
        a => props.labelList.find(b => b.id === a.labelId) !== undefined
      ),
    [props.annotationList, props.labelList]
  );

  const annotationList$ = useValueAsDataStream(annotationListFiltered);
  const imageList$ = useValueAsDataStream(props.imageList);
  const labelList$ = useValueAsDataStream(props.labelList);
  const imageInfo$ = useValueAsDataStream(props.image);

  /* events */

  const [closeSnackbarClick$, onCloseSnackbarClick] = useVoidEventAsStream();

  const [
    // dummy comment to control prettier
    postResultType$,
    setPostResultType
  ] = useEventAsStream<'success' | 'error'>();

  const [inferClick$, onInferClick] = useVoidEventAsStream();

  const [
    // dummy comment to control prettier
    inferenceRawResult$,
    setInferenseRawResult
  ] = useEventAsStream<IList<IAnnotationType> | 'error'>();

  const [appMode$, setAppMode] = useStateAsStream<AppMode>('crop-image');

  /* values */

  const inferenceResult$ = useStream<IList<IBboxType>>(
    inferenceRawResult$.pipe(
      withLatestFrom(labelList$),
      map(([res, labelList]) =>
        res === 'error'
          ? undefined
          : res.map(annotation =>
              IBbox({
                id: annotation.uuid,
                label: labelList.find(l => l.id === annotation.labelId),
                rect: annotation.rect
              })
            )
      ),
      filterNotUndefined()
    )
  );

  const imageIndex$ = useDataStream<number>(
    0,
    combineLatestTyped(imageList$, imageId$).pipe(
      map(([list, id]) => list.findIndex(e => e.id === id) ?? 0)
    )
  );

  const image$ = useDataStream<IImageType | undefined>(
    undefined,
    imageInfo$.pipe(
      map(imageInfo =>
        IImage({
          src: imageInfo.fileUri,
          rectSize: imageInfo.size
        })
      )
    )
  );

  const cropRectRelativeInput$ = useDataStream<IRectType | undefined>(
    undefined,
    imageInfo$.pipe(
      map(image => toRelativeRect(IRect(image.size))(image.cropRect))
    )
  );

  const {
    cropRectRelativeFromCanvas$,
    setCropRectRelativeFromCanvas,
    cropRectRelative
  } = useCropRect(cropRectRelativeInput$, imageInfo$, setPostResultType);

  const bboxRelativeListInput$ = useDataStream<IList<IBboxType> | undefined>(
    undefined,
    combineLatestTyped(
      annotationList$,
      labelList$,
      imageInfo$.pipe(map(image => toRelativeRect(IRect(image.size))))
    ).pipe(
      map(([annotationList, labelList, toRectRelativeToImageFn]) =>
        annotationList.map(an =>
          IBbox({
            id: an.uuid,
            rect: toRectRelativeToImageFn(an.rect),
            label: labelList.find(l => l.id === an.labelId)
          })
        )
      )
    )
  );

  const {
    bboxRelativeListFromCanvas$,
    setBboxRelativeListFromCanvas,
    bboxRelativeList$
  } = useBboxList(
    bboxRelativeListInput$,
    inferenceResult$,
    setPostResultType,
    imageInfo$
  );

  const inferenceResultType$ = useStream<'success' | 'error'>(
    inferenceRawResult$.pipe(
      map(res => (res === 'error' ? 'error' : 'success'))
    )
  );

  const isWaitingServerResponse$ = useDataStream<boolean>(
    false,
    mergeTyped(
      mergeTyped(
        bboxRelativeListFromCanvas$,
        cropRectRelativeFromCanvas$,
        inferClick$
      ).pipe(mapTo(true)),
      mergeTyped(postResultType$, inferenceResultType$).pipe(mapTo(false))
    )
  );

  const navButtonsIsDisabled$ = useDataStream<{
    firstPage: boolean;
    prevPage: boolean;
    nextPage: boolean;
    lastPage: boolean;
  }>(
    { firstPage: false, prevPage: false, nextPage: false, lastPage: false },
    isWaitingServerResponse$.pipe(
      map(b => ({
        firstPage: b,
        prevPage: b,
        nextPage: b,
        lastPage: b
      }))
    )
  );

  const snackbarState$ = useDataStream<PromiseSnackbarStateType>(
    {
      isOpen: false,
      message: '',
      promiseState: 'pending'
    },
    mergeTyped(
      mergeTyped(bboxRelativeListFromCanvas$, cropRectRelativeFromCanvas$).pipe(
        mapTo({
          isOpen: true,
          message: snackbarMessages.save.pending,
          promiseState: 'pending' as const
        })
      ),
      postResultType$.pipe(
        toPromiseSnackbarState(
          2000,
          resultType => ({
            isOpen: true,
            message:
              resultType === 'success'
                ? snackbarMessages.save.success
                : snackbarMessages.save.error,
            promiseState: resultType
          }),
          resultType => ({
            isOpen: false,
            message: '',
            promiseState: resultType
          })
        )
      ),
      inferClick$.pipe(
        mapTo({
          isOpen: true,
          message: snackbarMessages.inference.pending,
          promiseState: 'pending' as const
        })
      ),
      inferenceResultType$.pipe(
        toPromiseSnackbarState(
          2000,
          resultType => ({
            isOpen: true,
            message:
              resultType === 'success'
                ? snackbarMessages.inference.success
                : snackbarMessages.inference.error,
            promiseState: resultType
          }),
          resultType => ({
            isOpen: false,
            message: '',
            promiseState: resultType
          })
        )
      ),
      closeSnackbarClick$.pipe(
        mapTo({
          isOpen: false,
          message: snackbarMessages.save.pending,
          promiseState: 'pending' as const
        })
      )
    )
  );

  const inferButtonState$ = useDataStream<ButtonStateType>(
    'disabled',
    mergeTyped(
      appMode$.pipe(distinctUntilChanged()),
      inferClick$.pipe(mapToConst('infer-click')),
      inferenceResult$.pipe(
        filterNotUndefined(),
        mapToConst('inference-result')
      )
    ).pipe(
      scan(inferButtonStateReducer, {
        appMode: 'crop-image',
        buttonState: 'disabled' as const
      }),
      get('buttonState')
    )
  );

  /* side effects */

  useStreamEffect(
    inferClick$.pipe(
      withLatestValuesFrom(token$, projectId$, imageInfo$, labelList$)
    ),
    ([_, [token, projectId, image, labelList]]) => {
      if (USE_MOCK_INFERENCE_RESULT) {
        setInferenseRawResult(generateMockInferenceResult(labelList));
      } else {
        if (token === undefined) return;
        executeInference(token)(projectId, image.id).then(result => {
          if (result.type === 'error') {
            setInferenseRawResult('error');
          } else {
            setInferenseRawResult(
              result.body.map(e =>
                e.update('rect', toRelativeRect(IRect(image.size)))
              )
            );
          }
        });
      }
    }
  );

  /*  values for view */

  const image = useStreamValue(image$);
  const bboxRelativeList = useStreamValue(bboxRelativeList$);
  const snackbarState = useStreamValue(snackbarState$);
  const inferButtonState = useStreamValue(inferButtonState$);
  const currentPageIndex = useStreamValue(imageIndex$, 1);
  const navButtonsIsDisabled = useStreamValue(navButtonsIsDisabled$);

  const { onPageIndexChange, onBackToListPageClick } = useHandlers(
    props.imageList
  );

  useLoadingPage(
    image === undefined ||
      cropRectRelative === undefined ||
      bboxRelativeList === undefined ||
      navButtonsIsDisabled === undefined
  );

  /*  contexts */

  const parentInfo: ParentType = 'project'

  return (
    <Root>
      {image === undefined ||
      cropRectRelative === undefined ||
      bboxRelativeList === undefined ||
      navButtonsIsDisabled === undefined ? (
        undefined
      ) : (
        <ParentContext.Provider value={parentInfo}>
          <RectToolWithCropMainSub
            image={image}
            cropRectRelative={cropRectRelative}
            bboxRelativeList={bboxRelativeList}
            labelList={props.labelList}
            navButtonsIsDisabled={navButtonsIsDisabled}
            currentPageIndex={currentPageIndex}
            pageLength={props.imageList.size}
            onPageIndexChange={onPageIndexChange}
            onBackToListPageClick={onBackToListPageClick}
            onBboxesRelativeChange={setBboxRelativeListFromCanvas}
            onCropRectRelativeChange={setCropRectRelativeFromCanvas}
            openSettingsDialogButtonState={'show'}
            openSettingsDialog={props.openSettingsDialog}
            onInferClick={onInferClick}
            inferButtonState={inferButtonState}
            onAppModeChange={setAppMode}
          />
        </ParentContext.Provider>
      )}
      <SnackbarForPromise
        isOpen={snackbarState?.isOpen ?? false}
        onCloseClick={onCloseSnackbarClick}
        message={snackbarState?.message ?? ''}
        promiseState={snackbarState?.promiseState}
      />
    </Root>
  );
});

ProjectImageRootSub.displayName = 'ProjectImageRootSub';
